/** Turkish-first formatting (AGENTS.md: `1.234,56` money, `DD.MM.YYYY` dates). */
import { t } from '@bia-auction/shared-i18n';

const money = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const integer = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 0 });
// Explicit parts, not `dateStyle: 'short'`: tr-TR's CLDR short pattern is `d.MM.y`
// (non-padded day), so `dateStyle` renders `5.09.2026` — not the `DD.MM.YYYY` this
// file promises — and freezes the output to the runner's ICU version.
const dateTime = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});
const dateOnly = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Integer minor units → localized money. Defaults to TRY (`1.234,56 ₺`); pass a lot/vehicle
 * `currency` (e.g. `EUR`) to format a non-TRY amount correctly. Null/undefined → em dash.
 */
export function formatKurus(minor: number | null | undefined, currency = 'TRY'): string {
  if (minor === null || minor === undefined) return '—';
  const value = minor / 100;
  if (currency === 'TRY') return `${money.format(value)} ₺`;
  try {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency }).format(value);
  } catch {
    return `${money.format(value)} ${currency}`;
  }
}

/**
 * A currency's display symbol for labelling amount inputs (`₺` for TRY, `€`/`$` for EUR/USD,
 * else the ISO code) — so a prompt asking for a EUR bid doesn't tell the operator to enter lira.
 */
export function currencySymbol(currency = 'TRY'): string {
  if (currency === 'TRY') return '₺';
  try {
    const part = new Intl.NumberFormat('tr-TR', { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? currency;
  } catch {
    return currency;
  }
}

/** Plain count → Turkish digit grouping (`1234` → `1.234`; AGENTS.md number format). */
export function formatCount(n: number): string {
  return integer.format(n);
}

/** Epoch-ms → `DD.MM.YYYY HH:mm`. */
export function formatDateTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  return dateTime.format(new Date(ms));
}

/** Epoch-ms → `DD.MM.YYYY`. */
export function formatDate(ms: number | null | undefined): string {
  if (!ms) return '—';
  return dateOnly.format(new Date(ms));
}

const timeOnly = new Intl.DateTimeFormat('tr-TR', { timeStyle: 'short' });

/** Epoch-ms → `HH:mm` (the autosave indicator's timestamp). */
export function formatTime(ms: number | null | undefined): string {
  if (!ms) return '—';
  return timeOnly.format(new Date(ms));
}

/**
 * Milliseconds remaining → `mm:ss`, `h:mm:ss` past an hour, or `Ng hh:mm:ss` past a
 * day. Clamps at zero. Kept in lockstep with the bidder's copy
 * (`apps/web-bidder/src/lib/format.ts`) — the live console and the showcase show the
 * same "time left on a sale", so they must not disagree on its shape.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  if (d > 0) return `${d}${t('common.dayShort')} ${String(h).padStart(2, '0')}:${mm}:${ss}`;
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}
